// ── Company-ID gate contract (Athena Build 3, frozen) ─────────────────────
// Mirrors metadata.gate_outcome exactly as emitted by Athena's /athena/chat
// SSE stream. Do NOT add fields speculatively — this is a consumed contract,
// not something this repo owns.

// Citation source types are owned by the leads askAica store (the Athena→FE
// contract lives on message.metadata.sources) — reused here so the portfolio
// surface's messages carry the identical shape (RECUR-27369).
import { CitationSource } from '../dashboard/askAica/citationTypes';
import { ReasoningStep } from '../dashboard/askAica/actions';

/** A single candidate offered by an ask_back(kind:"pick") gate. `city` is
 * ALWAYS null on the wire — never render it. */
export interface Candidate {
    org_id: string;
    name: string;
    cin?: string | null;
    city?: string | null;
}

export interface CurrentCompany {
    org_id: string;
    /** Athena may resolve a company by org_id without a display name in some
     * edge cases — the chip falls back to a placeholder rather than rendering
     * blank (see CompanyChip usage in AskAicaInputBox, F-15). */
    name: string | null;
}

/** Runtime values for GateOutcomeType — single source of truth so callers compare
 * against a named constant instead of a hand-typed string literal (human review #16). */
export const GATE_OUTCOME_TYPE = {
    RESOLVED: 'resolved',
    CONTINUE: 'continue',
    PORTFOLIO: 'portfolio',
    COMPANY_LESS: 'company_less',
    ASK_BACK: 'ask_back',
    DECLINE: 'decline',
} as const;

export type GateOutcomeType = (typeof GATE_OUTCOME_TYPE)[keyof typeof GATE_OUTCOME_TYPE];

export interface AskBackGate {
    kind: 'pick';
    prompt?: string;
    candidates: Candidate[];
}

export interface DeclineGate {
    /** Human-readable reason string from Athena — rendered verbatim (see DeclineNotice). */
    reason: string;
}

export interface GateOutcome {
    type: GateOutcomeType;
    current_company: CurrentCompany | null;
    ask_back?: AskBackGate;
    decline?: DeclineGate;
}

export interface GlobalMessage {
    id: number;
    type: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    /** Citation sources from Athena (final chunk only) — mirrors the leads
     * Message.metadata.sources shape so the reused CitationAnswer/SourcesPanel
     * render identically on the portfolio surface (RECUR-27369). */
    metadata?: {
        sources?: CitationSource[];
    };
    /** Athena H-11: history items may flag a gate ask-back PROMPT turn (multi-
     * candidate "I found multiple companies matching…" AND confirm-single "Did
     * you mean X? Please confirm.") structurally, instead of the FE guessing
     * off English copy via ASK_BACK_PROMPT_PREFIX (which never covered the
     * confirm-single phrasing). Optional/additive — undefined on live turns
     * (never persisted, see runTurn) and on older history rows the BE hasn't
     * backfilled; the prefix match stays as a fallback for those. Decline
     * turns are NOT flagged — Q7 keeps decline text as a plain bubble. */
    is_gate_control?: boolean;
}

/** A row in the user-scoped conversation list (GET /chat/conversations/user). */
export interface GlobalConversationListItem {
    conversation_id: string;
    user_id: string;
    user_name: string | null;
    title: string | null;
    last_message_at: string | null;
}

/** A conversation the user has navigated away from while its turn is still streaming.
 * Holds exactly the three fields that are per-conversation, so switching back can
 * restore the live turn instead of re-fetching over the top of it. */
export interface BackgroundThread {
    messages: GlobalMessage[];
    isLoading: boolean;
    reasoning?: ReasoningStep;
}

export interface GlobalAskAicaState {
    /** The ACTIVE (on-screen) conversation's messages. Background conversations live in
     * `background` — see BackgroundThread. */
    messages: GlobalMessage[];
    isLoading: boolean;
    isLoadingChatHistory: boolean;
    error: string | null;
    /** Streamed reasoning for the in-flight turn — mirrors the leads
     * askAicaReducer.reasoning shape so the reused ReasoningPanel works as-is. */
    reasoning?: ReasoningStep;

    /** The company the gate has currently resolved (or null when company-less). */
    currentCompany: CurrentCompany | null;
    /** True once the gate has explicitly told us this turn is company-less. */
    companyLess: boolean;
    /** Present only while an ask_back gate is open; null once resolved/declined. */
    askBack: AskBackGate | null;
    /** Present only while a decline gate is open. */
    decline: DeclineGate | null;

    /** Held across turns once captured from the stream; omitted on the very first turn. */
    conversationId: string | null;

    conversations: GlobalConversationListItem[];
    isLoadingConversations: boolean;

    selectedSources: string[];

    /** True while "Delete Chat" is clearing the current conversation's memory (F-7). */
    isClearingChat: boolean;
    /** True when the last loaded history page was full — older messages may exist (F-9). */
    hasMoreData: boolean;

    /** True from New Chat click until the next turn's first conversation_id
     * lands (D1/D2) — see actions.ts::setPendingNewConversation. */
    pendingNewConversation: boolean;

    /** Turns still streaming in conversations the user has navigated away from, keyed by
     * conversation id (or DRAFT_CONVERSATION_KEY). Populated on switch-away, drained on
     * switch-back, so a response keeps building while you read something else. */
    background: Record<string, BackgroundThread>;
}
